/* eslint-disable @typescript-eslint/no-explicit-any */
import { Input } from "@/shadcnComponents/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/shadcnComponents/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shadcnComponents/ui/table";
import { Button } from "@/shadcnComponents/ui/button";

import { useState, useMemo } from "react";
import { OpenInNewWindow, getRandomProfileEmoji } from "@/Helpers/helper";
import { getDate } from "./AdminUtils";
import { useNavigate } from "react-router-dom";

export const AdminInfluencerTable = ({ allcreators, handleClick }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState({
    followers: "",
    totalChips: "",
    extensionUsed: "",
    badge: "",
    activePlan: "",
    slug: "",
  });
  const [sortConfig, setSortConfig] = useState({ column: "", order: "asc" });

  // Apply filters and sorting to the creators data
  const filteredAndSortedCreators = useMemo(() => {
    let filteredData = [...allcreators];

    // Filtering logic
    if (filter.followers) {
      filteredData = filteredData.filter(
        (creator) => parseInt(creator.followers) >= parseInt(filter.followers)
      );
    }
    if (filter.totalChips) {
      filteredData = filteredData.filter(
        (creator) => parseInt(creator.totalChips) >= parseInt(filter.totalChips)
      );
    }
    if (filter.extensionUsed) {
      filteredData = filteredData.filter(
        (creator) =>
          (creator.ExtensionUsed ? "Yes" : "No") === filter.extensionUsed
      );
    }
    if (filter.badge) {
      filteredData = filteredData.filter((creator) =>
        creator.badge?.toLowerCase().includes(filter.badge.toLowerCase())
      );
    }
    if (filter.activePlan) {
      filteredData = filteredData.filter(
        (creator) => (creator.activePlan ? "Yes" : "No") === filter.activePlan
      );
    }
    if (filter.slug) {
      filteredData = filteredData.filter((creator) =>
        creator.slug?.toLowerCase().includes(filter.slug.toLowerCase())
      );
    }

    // Sorting logic
    if (sortConfig.column) {
      filteredData.sort((a, b) => {
        const order = sortConfig.order === "asc" ? 1 : -1;
        const aValue =
          sortConfig.column === "followers"
            ? parseInt(a[sortConfig.column] || 0)
            : a[sortConfig.column];
        const bValue =
          sortConfig.column === "followers"
            ? parseInt(b[sortConfig.column] || 0)
            : b[sortConfig.column];

        if (aValue == null) return 1; // Place null/undefined values at the end
        if (bValue == null) return -1;

        if (typeof aValue === "string") {
          return aValue.localeCompare(bValue) * order;
        }
        return (aValue - bValue) * order;
      });
    }

    return filteredData;
  }, [allcreators, filter, sortConfig]);

  const handleFilterChange = (field, value) => {
    setFilter((prev) => ({ ...prev, [field]: value }));
  };

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      order: prev.column === column && prev.order === "asc" ? "desc" : "asc",
    }));
  };

  const exportToCSV = () => {
    const headers = [
      "S.No,Name,Email,Phone,Referred By,Followers,Total Chips,Badge,LinkedIn,Synced By,Extension Used",
    ];
    const rows = filteredAndSortedCreators.map(
      (item, index) =>
        `${index + 1},"${item.name}","${item.email}","${item.mobile}","${
          item.refered_by
        }","${item.followers}","${item.totalChips}","${item.badge}","${
          item.linkedinLink
        }","${item.SyncedBy}","${item.ExtensionUsed ? "Yes" : "No"}"`
    );
    const csvData = headers.concat(rows).join("\n");

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "creators_data.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="mb-6 p-4 shadow-lg rounded-lg bg-white border border-gray-200 flex gap-4 flex-wrap items-center">
        <span className="w-full text-center font-inter font-bold text-xs ">
          Add Filters{" "}
        </span>
        <Input
          type="number"
          name="followers"
          placeholder="Min Followers"
          value={filter.followers}
          onChange={(e) => handleFilterChange("followers", e.target.value)}
          className="w-full max-w-[160px] input-filter"
        />
        <Input
          type="number"
          name="totalChips"
          placeholder="Min Chips"
          value={filter.totalChips}
          onChange={(e) => handleFilterChange("totalChips", e.target.value)}
          className="w-full max-w-[160px] input-filter"
        />
        <Select
          name="extensionUsed"
          value={filter.extensionUsed}
          onValueChange={(value) => handleFilterChange("extensionUsed", value)}
        >
          <SelectTrigger className="w-full max-w-[160px]">
            Extension Used
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
        <Select
          name="activePlan"
          value={filter.activePlan}
          onValueChange={(value) => handleFilterChange("activePlan", value)}
        >
          <SelectTrigger className="w-full max-w-[160px]">
            Active Plan
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="text"
          name="badge"
          placeholder="Badge"
          value={filter.badge}
          onChange={(e) => handleFilterChange("badge", e.target.value)}
          className="w-full max-w-[160px] input-filter"
        />
        <Input
          type="text"
          name="slug"
          placeholder="slug"
          value={filter.slug}
          onChange={(e) => handleFilterChange("slug", e.target.value)}
          className="w-full max-w-[160px] input-filter"
        />
        <Button
          onClick={exportToCSV}
          className="bg-blue-500 text-white rounded-md shadow-md px-4 py-2 hover:bg-blue-600 transition duration-150"
        >
          Export to CSV
        </Button>
      </div>
      <Table className="min-w-[360px] w-full overflow-auto ">
        <TableHeader className="sticky top-0 bg-[#1e1e1e] z-10">
          <TableRow className="bg-[#1e1e1e] hover:bg-[#1e1e1e] hover:rounded-lg border-b border-[#262626]">
            <TableHead className="min-w-[80px] text-center">S. No.</TableHead>
            <TableHead
              onClick={() => handleSort("name")}
              className="text-start cursor-pointer min-w-[200px] max-w-[250px] "
            >
              Name{" "}
              {sortConfig.column === "name" &&
                (sortConfig.order === "asc" ? "↑" : "↓")}{" "}
            </TableHead>
            <TableHead className="text-start">Email</TableHead>
            <TableHead className="text-start">Phone</TableHead>
            <TableHead className="text-start">Referred By</TableHead>
            <TableHead
              className="text-start cursor-pointer"
              onClick={() => handleSort("followers")}
            >
              Followers{" "}
              {sortConfig.column === "followers" &&
                (sortConfig.order === "asc" ? "↑" : "↓")}
            </TableHead>
            <TableHead
              className="text-start cursor-pointer"
              onClick={() => handleSort("totalChips")}
            >
              Total Chips{" "}
              {sortConfig.column === "totalChips" &&
                (sortConfig.order === "asc" ? "↑" : "↓")}
            </TableHead>
            <TableHead className="text-start">Badge</TableHead>
            <TableHead className="text-start">LinkedIn</TableHead>
            <TableHead className="text-start">Synced By</TableHead>
            <TableHead className="text-start">Extension Used</TableHead>
            <TableHead className="text-start">Joined Date</TableHead>
            <TableHead className="text-start">ActivePlan</TableHead>
            <TableHead className="text-start">Slug</TableHead>
            <TableHead className="text-start">Id</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredAndSortedCreators.map((creator, index) => (
            <TableRow key={index} className=" hover:rounded-lg">
              <TableCell className="text-center">{index + 1}</TableCell>
              <TableCell
                onClick={() => handleClick(creator?._id, creator?.status)}
                className="flex justify-start overflow-hidden text-wrap items-center space-x-2 text-start cursor-pointer "
              >
                <img
                  src={creator.profile}
                  onError={(e: any) => {
                    const randomProfileEmoji = getRandomProfileEmoji();
                    e.target.onerror = null;
                    e.target.src = randomProfileEmoji;
                  }}
                  alt={`${creator?.name} Profile`}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <span>{creator?.name}</span>
              </TableCell>
              <TableCell className="text-start">{creator.email}</TableCell>
              <TableCell className="text-start">{creator.mobile}</TableCell>
              <TableCell className="text-start">{creator.refered_by}</TableCell>
              <TableCell className="text-start">{creator.followers}</TableCell>
              <TableCell className="text-start">{creator.totalChips}</TableCell>
              <TableCell className="text-start">{creator.badge}</TableCell>
              <TableCell className="text-start">
                {creator.linkedinLink ? (
                  <span
                    className="text-blue-500 underline cursor-pointer "
                    onClick={() =>
                      OpenInNewWindow({ link: creator.linkedinLink })
                    }
                  >
                    Profile
                  </span>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="text-start">{creator.SyncedBy}</TableCell>
              <TableCell className="text-start">
                {creator.ExtensionUsed ? "Yes" : "No"}
              </TableCell>
              <TableCell className="text-start">
                {getDate(creator?.createdAt)}
              </TableCell>
              <TableCell className="text-start">
                {creator?.activePlan ? "Yes" : "No"}
              </TableCell>
              <TableCell className="text-start">{creator?.slug}</TableCell>
              <TableCell className="text-start">
                <div className="flex flex-col gap-1 ">
                  {creator?._id}
                  <div className="flex gap-1 ">
                    {" "}
                    <span
                      onClick={() =>
                        navigate(`/developer/admin/pastCollabs/${creator?._id}`)
                      }
                      className="cursor-pointer text-blue-400 underline "
                    >
                      View Collab
                    </span>{" "}
                  </div>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};
